import { useState, useEffect } from 'react';
import { useI18n } from '../../../../i18n';
import { request } from '../../../shared/services/api';
import { useRequestsConfig } from '../hooks/useRequestsConfig';
import type { EscaladoRegla, TipoSolicitud } from '../types/requests.types';

interface PersonaOption {
  id: string;
  nombre: string;
  apellido: string;
}

interface EscaladoForm {
  tipoSolicitudId: string;
  horasLimite: number;
  escaladoAId: string;
  nivelEscalado: number;
  activo: boolean;
}

const EMPTY_FORM: EscaladoForm = {
  tipoSolicitudId: '',
  horasLimite: 24,
  escaladoAId: '',
  nivelEscalado: 1,
  activo: true,
};

export function EscaladosTab() {
  const { t } = useI18n();
  const { items, loading, error, crear, actualizar, eliminar, refetch } =
    useRequestsConfig<EscaladoRegla>('/requests/escalados');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<EscaladoForm>(EMPTY_FORM);
  const [personas, setPersonas] = useState<PersonaOption[]>([]);
  const [tipos, setTipos] = useState<TipoSolicitud[]>([]);

  useEffect(() => {
    request<{ data: PersonaOption[] }>('/apps/personas')
      .then((res) => setPersonas(res.data))
      .catch(() => setPersonas([]));
    request<{ data: TipoSolicitud[] }>('/requests/tipos')
      .then((res) => setTipos(res.data))
      .catch(() => setTipos([]));
  }, []);

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (item: EscaladoRegla) => {
    setEditId(item.id);
    setForm({
      tipoSolicitudId: item.tipoSolicitudId,
      horasLimite: item.horasLimite,
      escaladoAId: item.escaladoAId,
      nivelEscalado: item.nivelEscalado,
      activo: item.activo,
    });
    setShowModal(true);
  };

  const close = () => setShowModal(false);

  const save = async () => {
    if (editId) {
      await actualizar(editId, form);
    } else {
      await crear(form);
    }
    close();
  };

  const handleDelete = async (id: string) => {
    await eliminar(id);
    refetch();
  };

  const personaName = (p?: { nombre: string; apellido: string }) =>
    p ? `${p.nombre} ${p.apellido}` : '-';

  if (loading) {
    return <div style={{ padding: 32, color: '#6B7280' }}>{t.requests.cargando}</div>;
  }

  if (error) {
    return <div style={{ padding: 32, color: '#EF4444' }}>{error}</div>;
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 4,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #D1D5DB',
    fontSize: 14,
    outline: 'none',
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F3864', margin: 0 }}>
          {t.requests?.escalados ?? 'Escalados'}
        </h3>
        <button
          onClick={openNew}
          style={{
            padding: '8px 20px',
            borderRadius: 8,
            border: 'none',
            background: '#1F3864',
            color: '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {t.requests?.nuevoEscalado ?? 'Nuevo escalado'}
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.tipoSolicitud ?? 'Tipo solicitud'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.horasLimite ?? 'Horas l\u00edmite'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.escaladoA ?? 'Escalado a'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.nivelEscalado ?? 'Nivel'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.activo ?? 'Activo'}
            </th>
            <th
              style={{
                textAlign: 'right',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.acciones ?? 'Acciones'}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>
                {tipos.find((tp) => tp.id === item.tipoSolicitudId)?.nombre ?? '-'}
              </td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>{item.horasLimite}h</td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>{personaName(item.escaladoA)}</td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>{item.nivelEscalado}</td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: item.activo ? '#22C55E' : '#D1D5DB',
                  }}
                />
              </td>
              <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                <button
                  onClick={() => openEdit(item)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2F5496',
                    cursor: 'pointer',
                    marginRight: 8,
                    fontSize: 13,
                  }}
                >
                  {t.requests?.editar ?? 'Editar'}
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#EF4444',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  {t.requests?.eliminar ?? 'Eliminar'}
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td
                colSpan={6}
                style={{
                  padding: '32px 16px',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: 14,
                }}
              >
                {t.requests?.sinEscalados ?? 'No hay reglas de escalado configuradas'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              width: 480,
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F3864', margin: '0 0 20px' }}>
              {editId
                ? (t.requests?.editarEscalado ?? 'Editar escalado')
                : (t.requests?.nuevoEscalado ?? 'Nuevo escalado')}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>{t.requests?.tipoSolicitud ?? 'Tipo solicitud'}</label>
                <select
                  value={form.tipoSolicitudId}
                  onChange={(e) => setForm({ ...form, tipoSolicitudId: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">{t.requests?.seleccionar ?? 'Seleccionar...'}</option>
                  {tipos.map((tp) => (
                    <option key={tp.id} value={tp.id}>
                      {tp.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>{t.requests?.horasLimite ?? 'Horas l\u00edmite'}</label>
                  <input
                    type="number"
                    min={1}
                    value={form.horasLimite}
                    onChange={(e) => setForm({ ...form, horasLimite: Number(e.target.value) })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t.requests?.nivelEscalado ?? 'Nivel escalado'}</label>
                  <input
                    type="number"
                    min={1}
                    value={form.nivelEscalado}
                    onChange={(e) => setForm({ ...form, nivelEscalado: Number(e.target.value) })}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>{t.requests?.escaladoA ?? 'Escalado a'}</label>
                <select
                  value={form.escaladoAId}
                  onChange={(e) => setForm({ ...form, escaladoAId: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">{t.requests?.seleccionar ?? 'Seleccionar...'}</option>
                  {personas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} {p.apellido}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  id="escalado-activo"
                />
                <label htmlFor="escalado-activo" style={{ fontSize: 14, color: '#374151' }}>
                  {t.requests?.activo ?? 'Activo'}
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={close}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.cancelar ?? 'Cancelar'}
              </button>
              <button
                onClick={save}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#1F3864',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.common.guardar}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
